import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { m, LazyMotion, domAnimation } from 'framer-motion';
import { useLocale } from '@/lib/i18n';
import { useSectionTranslation } from '@/hooks';
import { CategoryLogo } from '../Hero/CategoryLogo';
import { logger } from '@/lib/logger';
import { fadeInUp } from '@/motion/variants';
import {
  formatCount,
  pluralize,
  type MarketplaceCategory,
} from '../useMarketplaceHome';
import { categoryGridTranslations } from './translations';

interface CategoryGridProps {
  categories: MarketplaceCategory[];
  loading: boolean;
}

/**
 * `CatalogApp.category` is the only grouping the schema models, so the tabs are
 * derived from the data instead of hardcoded. The previous build shipped a
 * fixed 8-tab taxonomy (currency / skins / coaching / …) that no column backed,
 * so `perType` was always `{}`, every non-`all` tab counted 0, and
 * `visibleTabs` hid all of them.
 *
 * Labels come from the locale file, keyed by the raw category value, with a
 * passthrough so an admin-created category still gets a tab.
 */
const CATEGORY_LABEL_KEYS = ['game', 'service', 'parent'] as const;

/** Every tab key the locale file must provide a label for. */
type TabLabel = 'all' | (typeof CATEGORY_LABEL_KEYS)[number] | 'other';

export function CategoryGrid({ categories, loading }: CategoryGridProps) {
  const { locale } = useLocale();
  const t = useSectionTranslation(categoryGridTranslations);
  const [activeTab, setActiveTab] = useState<TabLabel>('all');

  const tabKey = (category: string) =>
    (CATEGORY_LABEL_KEYS as readonly string[]).includes(category)
      ? (category as (typeof CATEGORY_LABEL_KEYS)[number])
      : 'other';

  // 'all' plus one tab per distinct category actually present in the payload.
  const tabs = useMemo<{ key: TabLabel; count: number }[]>(() => {
    const present = new Set(categories.map((app) => tabKey(app.category)));
    return [
      { key: 'all', count: categories.reduce((n, c) => n + c.liveCount, 0) },
      ...CATEGORY_LABEL_KEYS.filter((k) => present.has(k)).map((k) => ({
        key: k,
        count: categories
          .filter((c) => tabKey(c.category) === k)
          .reduce((n, c) => n + c.liveCount, 0),
      })),
      ...(present.has('other')
        ? [
            {
              key: 'other' as const,
              count: categories
                .filter((c) => tabKey(c.category) === 'other')
                .reduce((n, c) => n + c.liveCount, 0),
            },
          ]
        : []),
    ];
  }, [categories]);

  const activeType = activeTab === 'all' ? null : activeTab;

  // Every active category is listed, not just ones with live offers — a fresh
  // install has zero offers and the grid used to render an empty state even
  // though 17 categories exist.
  // An app is exactly one `category`, so its `liveCount` is already the count
  // for whichever tab it lands in. No separate per-tab tally is needed.
  const visible = activeType === null
    ? categories
    : categories.filter((app) => tabKey(app.category) === activeType);

  if (!loading && categories.length === 0) return null;

  return (
    <LazyMotion features={domAnimation}>
      <section className="py-16 md:py-20" aria-labelledby="category-heading">
        <div className="container-main">
          <m.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="text-center mb-8 md:mb-10"
          >
            <span className="text-xs font-semibold uppercase tracking-widest text-accent mb-3 block">
              {t.eyebrow}
            </span>
            <h2 id="category-heading" className="font-display text-display-md text-brand-white mb-3">
              {t.title}
            </h2>
            <p className="text-body-md text-brand-muted max-w-2xl mx-auto">
              {t.subtitle}
            </p>
          </m.div>

          {tabs.length > 1 && (
            <div
              className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-6"
              role="tablist"
              aria-label={t.title}
            >
              {tabs.map((tab) => {
                const active = activeTab === tab.key;
                const label =
                  tab.key === 'all' ? t.tabs.all : t.tabs[tab.key];
                return (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setActiveTab(tab.key)}
                    className={`shrink-0 inline-flex items-center gap-2 px-4 h-9 rounded-full border text-sm font-medium transition-colors ${
                      active
                        ? 'bg-accent text-surface-base border-accent'
                        : 'bg-surface-card border-surface-border text-brand-muted hover:text-brand-white hover:border-accent/20'
                    }`}
                  >
                    {label}
                    {tab.count > 0 && (
                      <span
                        className={`text-[11px] ${active ? 'text-surface-base/70' : 'text-brand-muted'}`}
                      >
                        {formatCount(tab.count, locale)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" aria-hidden="true">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="h-[88px] rounded-xl bg-surface-card border border-surface-border animate-pulse"
                />
              ))}
            </div>
          ) : visible.length === 0 ? (
            <p className="text-sm text-brand-muted py-6 text-center">{t.empty}</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {visible.map((app) => (
                <Link
                  key={app.id}
                  to={`/category/${app.slug}`}
                  onClick={() => logger.info('Category tile clicked', { slug: app.slug })}
                  className="group flex flex-col items-center justify-center gap-2.5 rounded-xl bg-surface-card border border-surface-border p-4 text-center hover:border-accent/30 hover:bg-surface-hover transition-colors"
                >
                  <div className="relative size-12 shrink-0 rounded-xl overflow-hidden bg-surface-hover border border-surface-border group-hover:border-accent/20 transition-colors">
                    <CategoryLogo
                      slug={app.slug}
                      name={app.name}
                      src={app.logo}
                      padding="p-1"
                    />
                  </div>
                  <div className="min-w-0 w-full">
                    <p className="text-sm font-medium text-brand-white truncate group-hover:text-accent transition-colors">
                      {app.name}
                    </p>
                    {app.liveCount > 0 && (
                      <p className="text-[11px] text-brand-muted">
                        {formatCount(app.liveCount, locale)} {pluralize(t.listings, app.liveCount, locale)}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </LazyMotion>
  );
}